import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  followUsers,
  followActivity,
  followNotificationsList,
  followDashboardStats,
  followUserTypeLabels,
  followUserTypeIcons,
  followUserTypeColors,
  getUsersByType,
  getUserById,
  getMostPopularUsers,
  getLiveUsers,
  getUnreadNotifications,
  getMutualFollowers,
  type FollowUser,
  type FollowUserType,
  type FollowNotification,
} from '@/mocks/rfwFollowUsers';

type PageState = 'loading' | 'error' | 'ready';
type ViewTab = 'descubrir' | 'siguiendo' | 'actividad' | 'notificaciones';

const FOLLOWED_USERS_KEY = 'rfw_followed_users';

function loadFollowedUsers(): string[] {
  try {
    const raw = localStorage.getItem(FOLLOWED_USERS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveFollowedUsers(ids: string[]) {
  localStorage.setItem(FOLLOWED_USERS_KEY, JSON.stringify(ids));
}

export default function SeguirUsuariosPage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [viewTab, setViewTab] = useState<ViewTab>('descubrir');
  const [typeFilter, setTypeFilter] = useState<FollowUserType | 'todos'>('todos');
  const [sortBy, setSortBy] = useState<'followers' | 'posts' | 'name'>('followers');
  const [followedIds, setFollowedIds] = useState<string[]>(() => loadFollowedUsers());
  const [selectedUser, setSelectedUser] = useState<FollowUser | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setPageState('ready'), 500);
    return () => clearTimeout(t);
  }, []);

  const toggleFollow = useCallback((userId: string) => {
    setFollowedIds((prev) => {
      const next = prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId];
      saveFollowedUsers(next);
      return next;
    });
  }, []);

  const isFollowing = useCallback((userId: string) => followedIds.includes(userId), [followedIds]);

  const filteredUsers = useMemo(() => {
    let result = getUsersByType(typeFilter);
    if (sortBy === 'followers') result.sort((a, b) => b.followersCount - a.followersCount);
    else if (sortBy === 'posts') result.sort((a, b) => b.postsCount - a.postsCount);
    else result.sort((a, b) => a.name.localeCompare(b.name));
    return result;
  }, [typeFilter, sortBy]);

  const liveUsers = getLiveUsers();
  const unreadNotifs = getUnreadNotifications();
  const mostPopular = getMostPopularUsers(8);
  const mutualUsers = getMutualFollowers();

  const followedUsers = useMemo(() => {
    return followUsers.filter((u) => followedIds.includes(u.id));
  }, [followedIds]);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    setTimeout(() => setPageState('ready'), 500);
  }, []);

  const typeTabs: { id: FollowUserType | 'todos'; label: string; icon: string }[] = [
    { id: 'todos', label: 'Todos', icon: 'ri-group-line' },
    { id: 'oyente', label: 'Oyentes', icon: followUserTypeIcons.oyente },
    { id: 'locutor', label: 'Locutores', icon: followUserTypeIcons.locutor },
    { id: 'moderador', label: 'Moderadores', icon: followUserTypeIcons.moderador },
    { id: 'creador', label: 'Creadores', icon: followUserTypeIcons.creador },
  ];

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-50">
        <section className="relative w-full h-[280px] md:h-[380px] bg-background-200 animate-pulse" />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 space-y-8">
          <div className="flex gap-2 flex-wrap">{[1,2,3,4,5].map((i) => (<div key={i} className="bg-background-100 rounded-full h-8 w-20 animate-pulse" />))}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3,4,5,6].map((i) => (<div key={i} className="bg-background-100 rounded-2xl h-[380px] animate-pulse" />))}</div>
        </main>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4 py-20">
        <div className="w-20 h-20 rounded-3xl bg-red-100 flex items-center justify-center mb-5">
          <i className="ri-error-warning-line text-3xl text-red-500"></i>
        </div>
        <h2 className="text-xl font-extrabold text-foreground-950 mb-2">Error al cargar la red social</h2>
        <p className="text-sm text-foreground-500 mb-6 text-center max-w-md">No pudimos cargar los datos. Verificá tu conexión e intentá de nuevo.</p>
        <button onClick={handleRetry} className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-refresh-line"></i>Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* HERO */}
      <section className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Large%20diverse%20group%20of%20people%20connecting%20and%20interacting%20at%20a%20modern%20social%20gathering%20warm%20sunset%20atmosphere%20genuine%20human%20connections%20authentic%20friendships%20forming%20beautiful%20community%20energy%20aerial%20perspective%20showing%20social%20network%20bonds%20editorial%20photography%20with%20emotional%20depth%20and%20warmth&width=1600&height=900&seq=rfw-seguir-usuarios-hero&orientation=landscape"
          alt="Red Social RFW"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-50/15 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/20">
            <i className="ri-group-line text-3xl text-background-50"></i>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
            Seguir Usuarios RFW
          </h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
            Oyentes, Locutores, Moderadores y Creadores. Seguí a las personas que hacen grande la radio. Construí tu red, compartí tu pasión y viví la comunidad como una verdadera red social.
          </p>
          <div className="flex items-center gap-3 mt-5">
            <span className="text-background-50/60 text-xs">{(followDashboardStats.totalFollows / 1000).toFixed(1)}K conexiones · {(followDashboardStats.activeFollowUsers / 1000).toFixed(1)}K usuarios activos</span>
          </div>
        </div>
      </section>

      {/* MAIN */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12 space-y-8">
        {/* STATS STRIP */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Total Conexiones', value: (followDashboardStats.totalFollows / 1000).toFixed(1) + 'K', icon: 'ri-group-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'Nuevas Semana', value: (followDashboardStats.weeklyNewFollows / 1000).toFixed(1) + 'K', icon: 'ri-user-add-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Promedio x Usuario', value: followDashboardStats.avgFollowsPerUser.toFixed(1), icon: 'ri-user-heart-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Mutuales', value: (followDashboardStats.mutualFollows / 1000).toFixed(1) + 'K', icon: 'ri-exchange-line', color: 'text-violet-600 bg-violet-50' },
            { label: 'Posts en Feed', value: (followDashboardStats.totalPostsFeed / 1000).toFixed(1) + 'K', icon: 'ri-article-line', color: 'text-sky-600 bg-sky-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* VIEW TABS */}
        <div className="flex items-center gap-1.5">
          {[
            { id: 'descubrir' as const, label: 'Descubrir Gente', icon: 'ri-compass-line', count: followUsers.length },
            { id: 'siguiendo' as const, label: 'Siguiendo', icon: 'ri-user-heart-line', count: followedIds.length },
            { id: 'actividad' as const, label: 'Actividad', icon: 'ri-flashlight-line', count: followActivity.length },
            { id: 'notificaciones' as const, label: 'Notificaciones', icon: 'ri-notification-3-line', count: unreadNotifs.length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setViewTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                viewTab === tab.id ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className={`${tab.icon} text-[10px]`}></i>
              {tab.label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${viewTab === tab.id ? 'bg-white/20 text-white' : 'bg-background-200 text-foreground-500'}`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* DESCUBRIR / SIGUIENDO / ACTIVIDAD / NOTIFICACIONES */}
        {(viewTab === 'descubrir' || viewTab === 'siguiendo') && (
          <>
            {/* TYPE FILTERS */}
            <div className="flex flex-wrap items-center gap-1.5">
              {typeTabs.map((tab) => {
                const isActive = typeFilter === tab.id;
                const typeInfo = tab.id !== 'todos' ? followUserTypeColors[tab.id as FollowUserType] : { badge: 'bg-background-100 text-foreground-600 border-background-200', text: 'text-foreground-600' };
                return (
                  <button
                    key={tab.id}
                    onClick={() => setTypeFilter(tab.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap border cursor-pointer ${
                      isActive ? `border-current shadow-sm ${typeInfo.text} ${typeInfo.badge}` : 'border-background-200 bg-background-50 text-foreground-500 hover:text-foreground-700 hover:border-background-300'
                    }`}
                  >
                    <i className={`${tab.icon} text-[10px]`}></i>
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* SORT + LIVE */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-1.5">
                {[
                  { id: 'followers' as const, label: 'Más Seguidos', icon: 'ri-group-line' },
                  { id: 'posts' as const, label: 'Más Posts', icon: 'ri-article-line' },
                  { id: 'name' as const, label: 'A-Z', icon: 'ri-sort-alphabet-asc' },
                ].map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSortBy(s.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      sortBy === s.id ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    <i className={`${s.icon} text-[10px]`}></i>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* LIVE NOW */}
            {liveUsers.length > 0 && viewTab === 'descubrir' && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                  </div>
                  <p className="text-xs font-bold text-foreground-900">En Cabina / Al Aire</p>
                  <span className="text-[10px] text-foreground-500">{liveUsers.length} activos</span>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {liveUsers.map((user) => {
                    const typeInfo = followUserTypeColors[user.type];
                    return (
                      <div key={user.id} className="flex-shrink-0 w-40 rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-red-300/60 transition-all duration-200">
                        <div className="relative h-20">
                          <img src={user.photoUrl} alt={user.name} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                          <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/90 text-white text-[8px] font-bold">
                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                            AL AIRE
                          </span>
                          <span className="absolute bottom-2 left-2 text-[9px] font-bold text-white">{user.name}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* RESULTS COUNT */}
            <p className="text-[11px] text-foreground-400">
              {filteredUsers.length === 0 ? 'No se encontraron usuarios.' : `Mostrando ${filteredUsers.length} usuario${filteredUsers.length !== 1 ? 's' : ''}`}
            </p>

            {/* GRID */}
            {filteredUsers.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(viewTab === 'siguiendo' ? filteredUsers.filter((u) => followedIds.includes(u.id)) : filteredUsers).map((user) => {
                  const following = isFollowing(user.id);
                  const typeInfo = followUserTypeColors[user.type];
                  return (
                    <div
                      key={user.id}
                      className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-rose-300/60 transition-all duration-300 group"
                    >
                      <div className="relative h-24 overflow-hidden">
                        <img src={user.coverUrl} alt={user.name} className="w-full h-full object-cover object-top" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                        {user.isLive && (
                          <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/90 text-white text-[9px] font-bold backdrop-blur-sm">
                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                            AL AIRE
                          </span>
                        )}
                        <span className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-[9px] font-medium border border-white/30 text-white bg-black/30 backdrop-blur-sm`}>
                          {followUserTypeLabels[user.type]}
                        </span>
                      </div>
                      <div className="px-4 pb-4 -mt-7 relative">
                        <div className={`w-14 h-14 rounded-full border-[3px] border-background-50 overflow-hidden ${typeInfo.ring} ring-2`}>
                          <img src={user.photoUrl} alt={user.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="mt-2">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <p className="text-sm font-bold text-foreground-900 line-clamp-1">{user.name}</p>
                            {user.isVerified && <i className="ri-verified-badge-fill text-xs text-sky-500"></i>}
                          </div>
                          <p className="text-[11px] text-foreground-500 mb-1">@{user.username.replace('@', '')}</p>
                          <p className="text-[11px] text-foreground-600 line-clamp-2 leading-relaxed mb-3">{user.bio}</p>
                          <div className="flex items-center gap-1 flex-wrap mb-3">
                            {user.badges.slice(0, 2).map((badge) => (
                              <span key={badge} className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[9px] font-medium">{badge}</span>
                            ))}
                          </div>
                          <div className="flex items-center justify-between text-[11px] mb-3">
                            <div className="flex items-center gap-3">
                              <span className="text-foreground-500"><strong className="text-foreground-900">{user.followersCount.toLocaleString()}</strong> seguidores</span>
                              <span className="text-foreground-500"><strong className="text-foreground-900">{user.followingCount}</strong> siguiendo</span>
                            </div>
                            <span className="text-foreground-400 flex items-center gap-1">
                              <i className="ri-map-pin-line text-[10px]"></i>
                              {user.city}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => toggleFollow(user.id)}
                              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                                following
                                  ? 'bg-rose-100 text-rose-700 border border-rose-200 hover:bg-rose-200'
                                  : 'bg-primary-500 text-white hover:bg-primary-600'
                              }`}
                            >
                              <i className={`${following ? 'ri-user-unfollow-line' : 'ri-user-add-line'} text-[11px]`}></i>
                              {following ? 'Siguiendo' : 'Seguir'}
                            </button>
                            <button
                              onClick={() => setSelectedUser(user)}
                              className="px-3 py-2 rounded-full bg-background-100 text-foreground-500 hover:text-foreground-700 hover:bg-background-200 text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-eye-line"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-user-unfollow-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-sm font-semibold text-foreground-400">No se encontraron usuarios</p>
                <p className="text-[12px] text-foreground-300 mt-1">Intentá ajustar los filtros</p>
              </div>
            )}
          </>
        )}

        {/* ACTIVIDAD */}
        {viewTab === 'actividad' && (
          <div className="space-y-4">
            <p className="text-[11px] text-foreground-400">Mostrando {followActivity.length} actividades recientes</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {followActivity.map((item) => (
                <div key={item.id} className="flex items-start gap-3 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-rose-200/60 transition-all duration-200">
                  <img src={item.userPhoto} alt={item.userName} className="w-10 h-10 rounded-full object-cover shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <p className="text-xs font-semibold text-foreground-900">{item.userName}</p>
                      <div className={`w-5 h-5 rounded-full ${item.iconBg} flex items-center justify-center`}>
                        <i className={`${item.icon} ${item.iconColor} text-[9px]`}></i>
                      </div>
                    </div>
                    <p className="text-[12px] text-foreground-600 leading-snug">{item.description}</p>
                    <p className="text-[10px] text-foreground-400 mt-1">{item.timestampRelative}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* NOTIFICACIONES */}
        {viewTab === 'notificaciones' && (
          <div className="space-y-4">
            <p className="text-[11px] text-foreground-400">{followNotificationsList.length} notificaciones · {unreadNotifs.length} sin leer</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {followNotificationsList.map((notif) => (
                <div key={notif.id} className={`flex items-start gap-3 p-4 rounded-xl border transition-all duration-200 ${
                  notif.isRead ? 'bg-background-50 border-background-200/70' : 'bg-background-50 border-rose-500/30 ring-1 ring-rose-100'
                }`}>
                  <div className={`w-10 h-10 rounded-full overflow-hidden shrink-0 ${!notif.isRead ? 'ring-2 ring-rose-300' : ''}`}>
                    <img src={notif.fromPhoto} alt={notif.fromName} className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-sm font-bold text-foreground-950 line-clamp-1">{notif.fromName}</p>
                      <span className="text-[10px] text-foreground-400 whitespace-nowrap shrink-0">{notif.timestamp}</span>
                    </div>
                    <p className="text-xs text-foreground-600 leading-snug mb-2">{notif.description}</p>
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full ${notif.bgColor} flex items-center justify-center`}>
                        <i className={`${notif.icon} ${notif.color} text-xs`}></i>
                      </div>
                      <span className="text-[10px] font-medium text-foreground-500">
                        {notif.type === 'nuevo-seguidor' ? 'Nuevo Seguidor' : notif.type === 'nuevo-logro' ? 'Nuevo Logro' : 'Nuevo Post'}
                      </span>
                      {!notif.isRead && (
                        <span className="ml-auto w-2 h-2 rounded-full bg-rose-500"></span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SIDEBAR SECTIONS */}
        {(viewTab === 'descubrir' || viewTab === 'siguiendo') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-5 rounded-2xl bg-background-100 border border-background-200/70">
              <h4 className="text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-star-line text-amber-600"></i>
                Usuarios Destacados
              </h4>
              <div className="space-y-2">
                {mostPopular.slice(0, 5).map((user) => (
                  <div key={user.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-background-50 border border-background-200/70">
                    <img src={user.photoUrl} alt={user.name} className="w-10 h-10 rounded-full object-cover shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1">
                        <p className="text-xs font-semibold text-foreground-900 line-clamp-1">{user.name}</p>
                        {user.isVerified && <i className="ri-verified-badge-fill text-[10px] text-sky-500"></i>}
                      </div>
                      <p className="text-[10px] text-foreground-500">{user.followersCount.toLocaleString()} seguidores · {followUserTypeLabels[user.type]}</p>
                    </div>
                    <button
                      onClick={() => toggleFollow(user.id)}
                      className={`px-3 py-1 rounded-full text-[10px] font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        isFollowing(user.id)
                          ? 'bg-rose-100 text-rose-700 border border-rose-200'
                          : 'bg-primary-500 text-white hover:bg-primary-600'
                      }`}
                    >
                      {isFollowing(user.id) ? 'Siguiendo' : 'Seguir'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-5 rounded-2xl bg-background-100 border border-background-200/70">
              <h4 className="text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-exchange-line text-violet-600"></i>
                Conexiones Mutuales
              </h4>
              {mutualUsers.length === 0 ? (
                <p className="text-xs text-foreground-500 text-center py-4">Todavía no tenés conexiones mutuales. ¡Empezá a seguir gente!</p>
              ) : (
                <div className="space-y-2">
                  {mutualUsers.slice(0, 5).map((user) => (
                    <div key={user.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-background-50 border border-background-200/70">
                      <img src={user.photoUrl} alt={user.name} className="w-10 h-10 rounded-full object-cover shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-foreground-900 line-clamp-1">{user.name}</p>
                        <p className="text-[10px] text-foreground-500">{followUserTypeLabels[user.type]} · {user.city}</p>
                      </div>
                      <span className="text-[10px] text-emerald-600 font-medium flex items-center gap-1">
                        <i className="ri-exchange-line text-[9px]"></i>
                        Mutual
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* CROSS LINKS */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/perfil-social-oyente" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-rose-300/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-user-line text-base text-rose-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Perfil Social</p>
            <p className="text-[10px] text-foreground-500 mt-1">Tu identidad en la comunidad RFW</p>
          </Link>
          <Link to="/rankings" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-amber-300/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-trophy-line text-base text-amber-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Rankings RFW</p>
            <p className="text-[10px] text-foreground-500 mt-1">Los usuarios más seguidos</p>
          </Link>
          <Link to="/centro-notificaciones" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-violet-300/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-notification-3-line text-base text-violet-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Centro de Notificaciones</p>
            <p className="text-[10px] text-foreground-500 mt-1">No te pierdas nada de tu red</p>
          </Link>
        </section>

        {/* REGLA FINAL */}
        <section className="p-6 rounded-2xl bg-rose-100/70 border border-rose-200/50 text-center">
          <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-heart-line text-xl text-white"></i>
          </div>
          <p className="text-lg font-extrabold text-foreground-950 mb-2">Debe sentirse como una verdadera red social.</p>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            Seguir a alguien no es solo apretar un botón. Es elegir conectar con esa persona, ver lo que publica, celebrar sus logros, y construir una relación real dentro de la comunidad radial. Cada nuevo seguidor, cada logro desbloqueado y cada post publicado son momentos que tejen la red más poderosa de la radio: la de las personas que la hacen posible. Oyentes, locutores, moderadores y creadores — todos parte de una misma comunidad, todos conectados por la pasión que compartimos: la radio en vivo.
          </p>
        </section>
      </main>

      {/* ─── MODAL DE PERFIL ─── */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedUser(null)}></div>
          <div className="relative bg-background-50 rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
            <button
              onClick={() => setSelectedUser(null)}
              className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-background-50/80 backdrop-blur-md flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line text-foreground-700"></i>
            </button>

            <div className="relative h-32 overflow-hidden rounded-t-3xl">
              <img src={selectedUser.coverUrl} alt={selectedUser.name} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              {selectedUser.isLive && (
                <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/90 text-white text-[10px] font-bold backdrop-blur-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                  AL AIRE
                </span>
              )}
            </div>

            <div className="px-5 pb-5 -mt-10 relative">
              <div className={`w-20 h-20 rounded-full border-[3px] border-background-50 overflow-hidden ${followUserTypeColors[selectedUser.type].ring} ring-2`}>
                <img src={selectedUser.photoUrl} alt={selectedUser.name} className="w-full h-full object-cover" />
              </div>

              <div className="mt-2">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <h2 className="text-lg font-extrabold text-foreground-950">{selectedUser.name}</h2>
                  {selectedUser.isVerified && <i className="ri-verified-badge-fill text-sm text-sky-500"></i>}
                </div>
                <p className="text-xs text-foreground-500 mb-1">@{selectedUser.username.replace('@', '')} · {followUserTypeLabels[selectedUser.type]}</p>
                <p className="text-xs text-foreground-600 leading-relaxed mb-3">{selectedUser.bio}</p>

                <div className="flex items-center gap-1 text-[11px] text-foreground-500 mb-3">
                  <i className="ri-map-pin-line text-[10px]"></i>
                  {selectedUser.city}, {selectedUser.country} {selectedUser.countryFlag}
                  <span className="mx-1.5">·</span>
                  <i className="ri-calendar-line text-[10px]"></i>
                  Miembro desde {selectedUser.joinedDate}
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="text-center p-3 rounded-xl bg-background-100">
                    <p className="text-lg font-extrabold text-foreground-900">{selectedUser.followersCount.toLocaleString()}</p>
                    <p className="text-[10px] text-foreground-500">Seguidores</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-background-100">
                    <p className="text-lg font-extrabold text-foreground-900">{selectedUser.followingCount}</p>
                    <p className="text-[10px] text-foreground-500">Siguiendo</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-background-100">
                    <p className="text-lg font-extrabold text-foreground-900">{selectedUser.postsCount}</p>
                    <p className="text-[10px] text-foreground-500">Posts</p>
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-wrap mb-3">
                  {selectedUser.badges.map((badge) => (
                    <span key={badge} className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[9px] font-medium">{badge}</span>
                  ))}
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-[10px] mb-1">
                      <span className="text-foreground-500">Nivel {selectedUser.level} — {selectedUser.levelName}</span>
                    </div>
                    <div className="w-full h-1.5 rounded-full bg-background-200">
                      <div className="h-full rounded-full bg-primary-500" style={{ width: `${Math.min(100, selectedUser.level * 5)}%` }}></div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => toggleFollow(selectedUser.id)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-bold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      isFollowing(selectedUser.id)
                        ? 'bg-rose-100 text-rose-700 border border-rose-200 hover:bg-rose-200'
                        : 'bg-primary-500 text-white hover:bg-primary-600'
                    }`}
                  >
                    <i className={`${isFollowing(selectedUser.id) ? 'ri-user-unfollow-line' : 'ri-user-add-line'}`}></i>
                    {isFollowing(selectedUser.id) ? 'Dejar de Seguir' : 'Seguir'}
                  </button>
                  <Link
                    to="/seguir-usuarios"
                    className="px-4 py-2.5 rounded-full bg-background-100 text-foreground-600 font-bold text-xs hover:bg-background-200 transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-1.5"
                  >
                    <i className="ri-user-line"></i>
                    Ver Perfil
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}